import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Set up axios to always include the token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('vault_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(data);
    } catch (error) {
      setUser(false);
      localStorage.removeItem('vault_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name }
      );
      if (data.token) {
        localStorage.setItem('vault_token', data.token);
      }
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password }
      );
      if (data.mfa_required) {
        setMfaRequired(true);
        setMfaToken(data.mfa_token);
        return { success: false, mfaRequired: true };
      }
      if (data.token) {
        localStorage.setItem('vault_token', data.token);
      }
      setUser(data);
      setMfaRequired(false);
      setMfaToken(null);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const loginWithMfa = async (code, isBackupCode = false) => {
    try {
      const body = { mfa_token: mfaToken };
      if (isBackupCode) {
        body.backup_code = code;
      } else {
        body.totp_code = code;
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login/mfa`,
        body
      );
      if (data.token) {
        localStorage.setItem('vault_token', data.token);
      }
      setUser(data);
      setMfaRequired(false);
      setMfaToken(null);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const cancelMfa = () => {
    setMfaRequired(false);
    setMfaToken(null);
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {});
      setUser(false);
      setMfaRequired(false);
      setMfaToken(null);
      localStorage.removeItem('vault_token');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithMfa, cancelMfa, mfaRequired, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
